<template>
  <PageContainer :breadcrumbs="breadcrumbs">
    <SingleContainer
      v-if="data"
      :variant-id="variantId"
      :data="data.pokemonSingleProduct"
      :same-pokemon-single-products="samePokemonSingleProducts"
      :same-pokemon-single-products-total="samePokemonSingleProductsTotal"
      @read-more-same-pokemon-single-products="fetchSamePokemonSingleProductsNextPage"
      :pre-evolution-pokemon-single-products="preEvolutionPokemonSingleProducts"
      :pre-evolution-pokemon-single-products-total="preEvolutionPokemonSingleProductsTotal"
      @read-more-pre-evolution-pokemon-single-products="fetchPreEvolutionPokemonSingleProductsNextPage"
      :evolution-pokemon-single-products="evolutionPokemonSingleProducts"
      :evolution-pokemon-single-products-total="evolutionPokemonSingleProductsTotal"
      @read-more-evolution-pokemon-single-products="fetchEvolutionPokemonSingleProductsNextPage"
      :pokemon-single-pickup-general-purpose-products
      :pokemon-single-pickup-basic-energy-products
    />
    <div class="ToPreviousPage">
      <TextButton v-if="navigationSource === 'internal'"text="前のページへ戻る" mode="arrow" @click="$router.back()" />
      <TextLink v-if="navigationSource === 'external'"text="ポケカ販売トップ" mode="arrow" to="/pokemon" />
    </div>
  </PageContainer>
</template>

<script lang="ts">
import { defineNuxtComponent, useAsyncData, useNuxtApp, createError, useRouter } from '#app'
import { definePageMeta, useSeoMeta, useHead, useRuntimeConfig } from '#imports'
import { DITokens } from '@/di'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'
import { TextLink, TextButton } from '@ka-nabellinc/ka-nabell-ui'
import type { ESPokemonSingleProduct } from '@ka-nabellinc/tcg-platform-es-schema'
import { usePokemonSingleRelatedProducts } from '@/composables/pokemon/usePokemonSingleRelatedProducts'
import SingleContainer from '@/containers/Pokemon/Single'
import PageContainer from '@/containers/Page'
import { PokemonSeries } from '@/const/pokemon'
import { getPokemonSingleProductSchema } from '@/utils/schema'
import { inventoryProductIdToVariantId } from '@/utils/marketing'

const PICKUP_PRODUCTS_LIMIT = 15

export default defineNuxtComponent({
  components: {
    PageContainer,
    SingleContainer,
    TextLink,
    TextButton,
  },
  async setup() {
    const route = useRouter().currentRoute.value
    const inventoryProductId = route.params.inventoryProductId as string
    const { $container } = useNuxtApp()
    const navigationStore = $container.resolve(DITokens.NavigationStore)
    const { navigationSource: _navigationSource } = storeToRefs(navigationStore)
    const navigationSource = ref<'external' | 'internal'>()

    definePageMeta({
      genre: 'pokemon',
      footer: {
        isShowSocialMedia: true,
        isShowShopSeries: true,
      },
    })

    const { data, error } = await useAsyncData(`pokemonSingleProduct_${inventoryProductId}`, async () => {
      const gateway = $container.resolve(DITokens.ElasticsearchGateway)
      const pokemonSingleProduct = await gateway.getPokemonSingleProduct(inventoryProductId)

      let samePokemonSingleProducts: ESPokemonSingleProduct[] = []
      let samePokemonSingleProductsTotal = 0
      let preEvolutionPokemonSingleProducts: ESPokemonSingleProduct[] = []
      let preEvolutionPokemonSingleProductsTotal = 0
      let evolutionPokemonSingleProducts: ESPokemonSingleProduct[] = []
      let evolutionPokemonSingleProductsTotal = 0
      let pokemonSinglePickupGeneralPurposeProducts: ESPokemonSingleProduct[] = []
      let pokemonSinglePickupBasicEnergyProducts: ESPokemonSingleProduct[] = []

      // 詳細ページの描画に必須では無いデータ(エラーを許容する)
      try {
        const RERATED_PRODUCTS_LIMIT = 3
        const {
          pm_card: {
            pm_card_pre_evolution_pokemon_unique_id,
            pm_card_pokemon_unique_id
          }
        } = pokemonSingleProduct

        // 同一カード検索
        const getSameProductsPromise = gateway.getSamePokemonSingleProducts(pokemonSingleProduct, RERATED_PRODUCTS_LIMIT, 0)

        // 進化前
        let getPreEvolutionProductsPromise = new Promise<{ hits: ESPokemonSingleProduct[]; total: number; }>(resolve => resolve({ hits: [], total: 0 }))
        if (pm_card_pre_evolution_pokemon_unique_id) {
          getPreEvolutionProductsPromise = gateway.getPokemonSingleProductsByPokemonUniqueId(pm_card_pre_evolution_pokemon_unique_id, RERATED_PRODUCTS_LIMIT, 0)
        }

        // 進化先
        let getEvolutionProductsPromise = new Promise<{ hits: ESPokemonSingleProduct[]; total: number; }>(resolve => resolve({ hits: [], total: 0 }))
        if (pm_card_pokemon_unique_id) {
          getEvolutionProductsPromise = gateway.getPokemonSingleProductsByPreEvolutionPokemonUniqueId(pm_card_pokemon_unique_id, RERATED_PRODUCTS_LIMIT, 0)
        }

        [
          { hits: samePokemonSingleProducts, total: samePokemonSingleProductsTotal },
          { hits: preEvolutionPokemonSingleProducts, total: preEvolutionPokemonSingleProductsTotal },
          { hits: evolutionPokemonSingleProducts, total: evolutionPokemonSingleProductsTotal },
          { hits: pokemonSinglePickupGeneralPurposeProducts },
          { hits: pokemonSinglePickupBasicEnergyProducts }
        ] = await Promise.all([
          getSameProductsPromise,
          getPreEvolutionProductsPromise,
          getEvolutionProductsPromise,
          gateway.getPokemonSinglePickupGeneralPurposeProducts(PICKUP_PRODUCTS_LIMIT),
          gateway.getPokemonSinglePickupBasicEnergyProducts(PICKUP_PRODUCTS_LIMIT),
        ])
      } catch (e) {
        // Do nothing
      }

      return {
        pokemonSingleProduct,
        samePokemonSingleProducts,
        samePokemonSingleProductsTotal,
        preEvolutionPokemonSingleProducts,
        preEvolutionPokemonSingleProductsTotal,
        evolutionPokemonSingleProducts,
        evolutionPokemonSingleProductsTotal,
        pokemonSinglePickupGeneralPurposeProducts,
        pokemonSinglePickupBasicEnergyProducts
      }
    })

    if (error.value || !data.value) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page not found',
        fatal: true
      })
    }

    const {
      samePokemonSingleProducts,
      samePokemonSingleProductsTotal,
      fetchSamePokemonSingleProductsNextPage,

      preEvolutionPokemonSingleProducts,
      preEvolutionPokemonSingleProductsTotal,
      fetchPreEvolutionPokemonSingleProductsNextPage,

      evolutionPokemonSingleProducts,
      evolutionPokemonSingleProductsTotal,
      fetchEvolutionPokemonSingleProductsNextPage
    } = usePokemonSingleRelatedProducts($container, data.value)

    const productNameStr = computed(() => {
      let _productNameStr = `${data.value?.pokemonSingleProduct.product_name} `

      if (data.value?.pokemonSingleProduct.pm_card.pack.expansion_mark) {
        _productNameStr += `[${data.value.pokemonSingleProduct.pm_card.pack.expansion_mark}]`
      }

      if (data.value?.pokemonSingleProduct.pm_card.collection_number) {
        _productNameStr += `[${data.value.pokemonSingleProduct.pm_card.collection_number}]`
      }

      _productNameStr += `[${data.value?.pokemonSingleProduct.pm_card.rarity.rarity_abbreviation}]`
      return _productNameStr
    })

    const title = computed(() => `${productNameStr.value}の通販 | 最安値＆即日発送ならカーナベル`)
    const description = `${productNameStr.value}の通販ならカーナベル！状態別に選べて、納得価格で購入可能。即日発送で最短翌日午前着！`

    const { public: { APP_ENV } } = useRuntimeConfig()
    useSeoMeta({
      title,
      ogTitle: title,
      description,
      ogDescription: description,
      robots: APP_ENV==='production' ? 'index,follow' :'noindex,nofollow',
      twitterSite: '@ka_nabell_pcg',
      ogSiteName: 'ポケカ販売ならカーナベル！',
      ogUrl: `https://sell.ka-nabell.com/pokemon/single/${inventoryProductId}`
    })

    const googleSchema = getPokemonSingleProductSchema(data.value.pokemonSingleProduct)

    useHead({
      link: [
        {
          rel: 'canonical',
          href: `https://sell.ka-nabell.com/pokemon/single/${inventoryProductId}`
        }
      ],
      script: [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(googleSchema, null, '\t')
        }
      ]
    })

    // Note: storeのreactive valueをそのままViewに反映すると遷移ボタン押下のタイミングで値が更新されてUIがチラつくため、mount時に1度だけ値を設定
    watch(_navigationSource, val => {
      if (!navigationSource.value) {
        navigationSource.value = val
      }
    }, { immediate: true })

    // Set Breadcrumbs
    const seriesId = data.value.pokemonSingleProduct.pm_card.series.series_id
    const packId = data.value.pokemonSingleProduct.pm_card.pack.pack_id
    const seriesNameStr = PokemonSeries[seriesId]
    const packNameStr = data.value.pokemonSingleProduct.pm_card.pack.pack_name
    const seriesPageLink = `/pokemon/series/${seriesId}`
    const packPageLink = `/pokemon/series/${seriesId}/packs/${packId}`

    const breadcrumbs = [
      {
        title: 'トレカ販売トップ',
        link: '/',
      },
      {
        title: 'ポケカ販売トップ',
        link: '/pokemon',
      },
      {
        title: seriesNameStr,
        link: seriesPageLink,
      },
      {
        title: packNameStr,
        link: packPageLink,
      },
      {
        title: productNameStr.value,
      },
    ]

    const variantId = inventoryProductIdToVariantId(inventoryProductId)

    if (process.client && typeof window !== 'undefined') {
      window.dataLayer.push({ variant_id: variantId })
    }

    return {
      variantId,
      data,
      navigationSource,
      breadcrumbs,

      samePokemonSingleProducts,
      samePokemonSingleProductsTotal,
      fetchSamePokemonSingleProductsNextPage,

      preEvolutionPokemonSingleProducts,
      preEvolutionPokemonSingleProductsTotal,
      fetchPreEvolutionPokemonSingleProductsNextPage,

      evolutionPokemonSingleProducts,
      evolutionPokemonSingleProductsTotal,
      fetchEvolutionPokemonSingleProductsNextPage,

      pokemonSinglePickupGeneralPurposeProducts: data.value.pokemonSinglePickupGeneralPurposeProducts,
      pokemonSinglePickupBasicEnergyProducts: data.value.pokemonSinglePickupBasicEnergyProducts
    }
  }
})
</script>
